import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";

interface Session {
  id: string;
  venue_name: string;
  device_name: string;
  status: string;
  verification_phrase: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
}

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "expired":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Expired
        </span>
      );
    case "cancelled":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
          Cancelled
        </span>
      );
    case "awaiting_payment":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
          Awaiting Payment
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status}
        </span>
      );
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function Home() {
  const [totalSessions, setTotalSessions] = useState<number | null>(null);
  const [totalEvents, setTotalEvents] = useState<number | null>(null);
  const [venueCount, setVenueCount] = useState<number | null>(null);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [sessionsRes, eventsRes, venuesRes, recentRes] = await Promise.all([
        apiClient["/api/v1/admin/sessions"]["get"]({
          parameters: { per_page: 1 },
          body: null,
        }),
        apiClient["/api/v1/admin/events"]["get"]({
          parameters: { per_page: 1 },
          body: null,
        }),
        apiClient["/api/v1/admin/venues"]["get"]({
          parameters: {},
          body: null,
        }),
        apiClient["/api/v1/admin/sessions"]["get"]({
          parameters: { per_page: 5 },
          body: null,
        }),
      ]);

      if (sessionsRes.code === 200) {
        const body = sessionsRes.body as { pagination: { total: number } };
        setTotalSessions(body.pagination.total);
      }
      if (eventsRes.code === 200) {
        const body = eventsRes.body as { pagination: { total: number } };
        setTotalEvents(body.pagination.total);
      }
      if (venuesRes.code === 200) {
        const body = venuesRes.body as unknown[];
        setVenueCount(body.length);
      }
      if (recentRes.code === 200) {
        const body = recentRes.body as { data: Session[] };
        setRecentSessions(body.data);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Fleet Dashboard</h1>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-surface-raised border border-border rounded-lg p-4">
          <p className="text-sm text-text-muted">Total Sessions</p>
          <p className="text-2xl font-bold text-text-primary mt-1">
            {loading ? "-" : (totalSessions ?? 0)}
          </p>
        </div>
        <div className="bg-surface-raised border border-border rounded-lg p-4">
          <p className="text-sm text-text-muted">Total Events</p>
          <p className="text-2xl font-bold text-text-primary mt-1">
            {loading ? "-" : (totalEvents ?? 0)}
          </p>
        </div>
        <div className="bg-surface-raised border border-border rounded-lg p-4">
          <p className="text-sm text-text-muted">Venues</p>
          <p className="text-2xl font-bold text-text-primary mt-1">
            {loading ? "-" : (venueCount ?? 0)}
          </p>
        </div>
      </div>

      {/* Recent sessions */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-text-primary">Recent Sessions</h2>
          <a
            href="/sessions"
            className="text-sm text-accent hover:text-accent-hover transition-colors no-underline"
          >
            View all sessions &rarr;
          </a>
        </div>

        {loading && <p className="text-text-muted text-sm">Loading...</p>}

        {!loading && recentSessions.length === 0 && (
          <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
            <p className="text-text-secondary">No sessions yet.</p>
          </div>
        )}

        {!loading && recentSessions.length > 0 && (
          <div className="grid gap-3">
            {recentSessions.map((s) => (
              <div
                key={s.id}
                className="bg-surface-raised border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div>
                    <div className="font-medium text-text-primary">{s.venue_name}</div>
                    <div className="text-sm text-text-muted">{s.device_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {statusBadge(s.status)}
                  <span className="text-sm text-text-muted">
                    {formatTime(s.starts_at ?? s.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
